'use client';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';

import { FilterCategories, FilterList } from '@/features/filters';
import { FilterType, SelectedFilterType } from '@/shared/config';
import { useModal } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import styles from './productFiltersMobile.module.scss';

interface FilterMobileProps {
  filters?: FilterType[];
  selectedFilters?: SelectedFilterType[];
  active?: boolean;
  close: () => void;
  onAccept: (value?: Record<number, number[]>) => void;
  onRemove: (arg: number, arg2: number) => void;
}

const ProductFiltersMobile: FC<FilterMobileProps> = (props) => {
  const {
    filters,
    active = false,
    close,
    selectedFilters: externalSelectedFilters,
    onAccept,
    onRemove,
  } = props;
  const [pendingFilterValues, setPendingFilterValues] = useState<
    Record<number, number[]>
  >({});

  const [chooseCategory, setChooseCategory] = useState<number | null>(null);
  const currentFilter = filters?.find(
    (item) => item.attribute_id === chooseCategory,
  );

  useModal({
    isOpen: active,
    onClose: close,
  });

  useEffect(() => {
    if (externalSelectedFilters?.length) {
      const initialFilters: Record<number, number[]> = {};
      externalSelectedFilters.forEach((filter) => {
        initialFilters[+filter.attribute_id] = filter.values;
      });
      setPendingFilterValues(initialFilters);
    } else {
      setPendingFilterValues({});
    }
  }, [externalSelectedFilters]);

  const getSelectedValuesForAttribute = (attributeId: number) => {
    return pendingFilterValues[attributeId] || [];
  };

  const handleFilterValueToggle = (attributeId: number, valueId: number) => {
    setPendingFilterValues((prev) => {
      const currentValues = prev[attributeId] || [];

      let newValues;
      if (currentValues.includes(valueId)) {
        newValues = currentValues.filter((id) => id !== valueId);
      } else {
        newValues = [...currentValues, valueId];
      }

      return {
        ...prev,
        [attributeId]: newValues,
      };
    });
  };

  const clearChooseCategory = () => {
    setChooseCategory(null);
  };

  const handleClose = () => {
    clearChooseCategory();
    close();
  };

  const handleAccept = () => {
    onAccept?.(pendingFilterValues);
    close();
  };

  const clearFilter = () => {
    onAccept?.();
    close();
  };

  return (
    <div
      className={classNames(
        styles.container,
        active && styles.container_active,
      )}
    >
      <div className={styles.container__wrapper}>
        <div className={styles.container__top}>
          {chooseCategory && (
            <button onClick={clearChooseCategory}>
              <Icon name="arrow" />
            </button>
          )}
          <p className={styles.container__title}>
            {currentFilter?.attribute_name ?? 'Фильтры'}
          </p>
          <button onClick={handleClose}>
            <Icon name="crossBig" />
          </button>
        </div>
        {currentFilter ? (
          <FilterList
            filter={currentFilter}
            selectedValues={getSelectedValuesForAttribute(
              currentFilter.attribute_id,
            )}
            onToggleValue={handleFilterValueToggle}
          />
        ) : (
          <FilterCategories
            filters={filters || []}
            selectedFilters={externalSelectedFilters}
            onClick={setChooseCategory}
            onRemove={onRemove}
          />
        )}
        <div className={styles.container__bottomBlock}>
          <button
            className={classNames(
              'button button--md-black',
              styles.container__btn,
            )}
            disabled={Object.values(pendingFilterValues).every(
              (values) => values.length === 0,
            )}
            onClick={handleAccept}
          >
            Применить
          </button>
          <button
            className={classNames(
              'button button--outline',
              styles.container__btn,
            )}
            disabled={
              !(externalSelectedFilters && externalSelectedFilters.length > 0)
            }
            onClick={clearFilter}
          >
            Сбросить все
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductFiltersMobile;
